import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar as CalendarIcon } from 'lucide-react';
import Layout from '@/components/Layout';
import { Calendar as CalendarUI } from '@/components/ui/calendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Calendar({ user, onLogout }) {
  const [holidays, setHolidays] = useState([]);
  const [leaveEvents, setLeaveEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/holidays`);
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  }, []);

  const fetchLeaveEvents = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leave/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching leave events:', error);
    }
  }, []);

  useEffect(() => {
    fetchHolidays();
    fetchLeaveEvents();
  }, [fetchHolidays, fetchLeaveEvents]);

  const isHoliday = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return holidays.some(h => h.date === dateStr);
  };

  const isOnLeave = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return leaveEvents.some((ev) => dateStr >= ev.start_date && dateStr <= ev.end_date);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8" data-testid="calendar-page">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Work Sans' }}>Dagatal</h1>

        <div className="grid lg:grid-cols-2 gap-8">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center">
                <CalendarIcon className="mr-2 h-5 w-5" />
                Dagatal
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-4">
              <CalendarUI
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-md border"
                modifiers={{
                  holiday: (date) => isHoliday(date),
                  onLeave: (date) => isOnLeave(date)
                }}
                modifiersStyles={{
                  holiday: {
                    color: 'red',
                    fontWeight: 'bold'
                  },
                  onLeave: {
                    backgroundColor: '#bfdbfe',
                    borderRadius: '9999px',
                    fontWeight: '600'
                  }
                }}
              />

              {/* Listi yfir þá sem eru í fríi/veikindi á völdum degi */}
              <div className="w-full max-w-md mt-4">
                <h3 className="text-sm font-semibold mb-2">Frí og veikindi þennan dag</h3>
                <div className="space-y-1 text-sm">
                  {selectedDate && leaveEvents
                    .filter((ev) => {
                      const d = selectedDate.toISOString().split('T')[0];
                      return d >= ev.start_date && d <= ev.end_date;
                    })
                    .map((ev) => (
                      <div
                        key={ev.id}
                        className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-md"
                      >
                        <span className="font-medium">{ev.employee_name || 'Óþekktur starfsmaður'}</span>
                        <span className="text-xs text-gray-500">
                          {ev.leave_type === 'vacation' ? 'Orlof' : 'Veikindi'}
                        </span>
                      </div>
                    ))}
                  {selectedDate && leaveEvents.filter((ev) => {
                    const d = selectedDate.toISOString().split('T')[0];
                    return d >= ev.start_date && d <= ev.end_date;
                  }).length === 0 && (
                    <p className="text-xs text-gray-500">Enginn í fríi eða veikindi þennan dag.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Íslensk frídaga 2025</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {holidays.map((holiday) => (
                  <div key={holiday.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <span className="font-medium">{holiday.name}</span>
                    <span className="text-sm text-gray-600">
                      {new Date(holiday.date).toLocaleDateString('is-IS')}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}